import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import jsPDF from "jspdf";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "@/hooks/use-toast";

interface TopKeyword {
  keyword: string;
  rank: number | null;
  url: string | null;
}

interface ReportData {
  pages_published: number;
  avg_rank: number | null;
  total_impressions: number | null;
  top_keywords: TopKeyword[];
  competitor_count: number;
  generated_at: string;
  week_start: string;
}

interface WeeklyReport {
  id: string;
  client_id: string;
  report_date: string;
  created_at: string;
  report_data: ReportData;
  ai_insight: string | null;
  ai_insight_generated_at: string | null;
}

const ReportsTab = ({ clientIds }: { clientIds: string[] }) => {
  const [reports, setReports] = useState<WeeklyReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [insightLoading, setInsightLoading] = useState(false);

  const load = async () => {
    if (clientIds.length === 0) {
      setReports([]);
      setLoading(false);
      return;
    }
    const { data } = await supabase
      .from("reports")
      .select("id, client_id, report_date, created_at, report_data, ai_insight, ai_insight_generated_at")
      .in("client_id", clientIds)
      .order("created_at", { ascending: false })
      .limit(20);
    if (data) setReports(data as unknown as WeeklyReport[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientIds.join(",")]);

  const handleGenerate = async () => {
    if (clientIds.length === 0) return;
    setGenerating(true);
    try {
      const results = await Promise.all(
        clientIds.map((cid) =>
          supabase.functions.invoke("generate-weekly-report", { body: { client_id: cid } })
        )
      );
      const ok = results.filter((r) => r.data?.success).length;
      if (ok === 0) {
        const first = results[0];
        toast({
          title: "Could not generate report",
          description: first.data?.error || first.error?.message || "Unknown error",
          variant: "destructive",
        });
      } else {
        toast({ title: `Report generated (${ok}/${results.length})` });
        await load();
      }
    } catch (err: any) {
      toast({ title: "Generation failed", description: err.message, variant: "destructive" });
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateInsight = async () => {
    const latest = reports[0];
    if (!latest) return;
    setInsightLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("generate-ai-insight", {
        body: { client_id: latest.client_id },
      });
      if (error || !data?.success) {
        toast({
          title: "Couldn't generate insight",
          description: data?.error || error?.message || "Unknown error",
          variant: "destructive",
        });
      } else {
        toast({
          title: data.cached ? "Showing recent insight" : "Insight generated",
        });
        await load();
      }
    } catch (err: any) {
      toast({ title: "Insight failed", description: err.message, variant: "destructive" });
    } finally {
      setInsightLoading(false);
    }
  };

  const downloadPdf = (report: WeeklyReport) => {
    const d = report.report_data;
    const pdf = new jsPDF({ unit: "pt", format: "letter" });
    const margin = 48;
    let y = margin;

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(20);
    pdf.text("Weekly SEO Report", margin, y);
    y += 22;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(10);
    pdf.setTextColor(120);
    pdf.text(`Generated: ${new Date(report.created_at).toLocaleString()}`, margin, y);
    y += 14;
    pdf.text(
      `Week starting: ${new Date(d.week_start).toLocaleDateString()}`,
      margin,
      y
    );
    y += 28;
    pdf.setTextColor(0);

    const stats: [string, string][] = [
      ["Pages published", String(d.pages_published)],
      ["Average ranking", d.avg_rank !== null ? `#${d.avg_rank}` : "—"],
      [
        "Total impressions (GSC)",
        d.total_impressions !== null ? d.total_impressions.toLocaleString() : "Not connected",
      ],
      ["Competitors tracked", String(d.competitor_count)],
    ];

    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    pdf.text("Summary", margin, y);
    y += 18;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    stats.forEach(([k, v]) => {
      pdf.setTextColor(110);
      pdf.text(k, margin, y);
      pdf.setTextColor(0);
      pdf.text(v, margin + 240, y);
      y += 18;
    });

    y += 14;
    pdf.setFont("helvetica", "bold");
    pdf.setFontSize(13);
    pdf.text("Top performing keywords", margin, y);
    y += 18;

    pdf.setFont("helvetica", "normal");
    pdf.setFontSize(11);
    if (d.top_keywords.length === 0) {
      pdf.setTextColor(140);
      pdf.text("No ranking data available for this period.", margin, y);
    } else {
      d.top_keywords.forEach((k, i) => {
        pdf.setTextColor(110);
        pdf.text(`${i + 1}.`, margin, y);
        pdf.setTextColor(0);
        pdf.text(k.keyword, margin + 22, y);
        pdf.setTextColor(80);
        pdf.text(k.rank !== null ? `#${k.rank}` : "—", margin + 360, y);
        y += 16;
      });
    }

    pdf.save(`seo-report-${report.report_date}.pdf`);
  };

  if (loading) {
    return (
      <div className="space-y-3">
        {[0, 1].map((i) => (
          <div key={i} className="h-32 bg-white/[0.02] rounded-lg animate-pulse border border-white/5" />
        ))}
      </div>
    );
  }

  const latest = reports[0];
  const previous = reports.slice(1);

  return (
    <div className="space-y-4">
      {/* Header / generate */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <p className="text-[10px] font-mono text-white/30 uppercase tracking-wider">Weekly Reports</p>
          <p className="text-sm font-mono text-white/70 mt-1">
            Snapshot of pages, rankings, impressions and competitors.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button
            onClick={handleGenerateInsight}
            disabled={insightLoading || !latest}
            className="text-xs font-mono px-3 py-1.5 rounded-md border border-purple-400/30 bg-gradient-to-r from-purple-500/10 to-fuchsia-500/10 text-purple-200 hover:from-purple-500/20 hover:to-fuchsia-500/20 transition-colors disabled:opacity-50 flex items-center gap-1.5"
          >
            <span className="text-[10px]">✨</span>
            {insightLoading ? "Thinking…" : "AI Weekly Insight"}
          </button>
          <button
            onClick={handleGenerate}
            disabled={generating || clientIds.length === 0}
            className="text-xs font-mono px-3 py-1.5 rounded-md border border-[#00ff88]/30 bg-[#00ff88]/5 text-[#00ff88] hover:bg-[#00ff88]/10 transition-colors disabled:opacity-50"
          >
            {generating ? "Generating…" : "Generate Report"}
          </button>
        </div>
      </div>

      {!latest ? (
        <div className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-6 text-center">
          <p className="text-sm text-white/50 font-mono">No reports yet.</p>
          <p className="text-xs text-white/20 font-mono mt-2">
            Click Generate Report to compile this week's snapshot.
          </p>
        </div>
      ) : (
        <>
          <ReportCard report={latest} highlight onDownload={() => downloadPdf(latest)} />
          {latest.ai_insight && (
            <AiInsightCard
              insight={latest.ai_insight}
              generatedAt={latest.ai_insight_generated_at}
            />
          )}
        </>
      )}

      {previous.length > 0 && (
        <div className="space-y-1.5">
          <p className="text-[10px] font-mono text-white/30 uppercase tracking-wider">Previous</p>
          {previous.map((r, i) => (
            <motion.div
              key={r.id}
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.03 }}
              className="bg-[#0a0a0a] border border-[#1a1a1a] rounded-lg p-3 flex items-center justify-between gap-2"
            >
              <div className="text-xs font-mono text-white/60">
                {new Date(r.created_at).toLocaleString()}
              </div>
              <div className="flex items-center gap-3 text-[10px] font-mono text-white/30">
                <span>{r.report_data.pages_published} pages</span>
                <span>•</span>
                <span>
                  {r.report_data.avg_rank !== null ? `avg #${r.report_data.avg_rank}` : "no ranks"}
                </span>
                <button
                  onClick={() => downloadPdf(r)}
                  className="ml-2 px-2 py-0.5 rounded border border-white/10 text-white/60 hover:text-white/90 hover:border-white/20"
                >
                  PDF
                </button>
              </div>
            </motion.div>
          ))}
        </div>
      )}
    </div>
  );
};

const ReportCard = ({
  report,
  highlight,
  onDownload,
}: {
  report: WeeklyReport;
  highlight?: boolean;
  onDownload: () => void;
}) => {
  const d = report.report_data;
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`bg-[#0a0a0a] border rounded-lg p-4 ${
        highlight ? "border-[#00ff88]/20" : "border-[#1a1a1a]"
      }`}
    >
      <div className="flex items-center justify-between mb-3">
        <p className="text-[10px] font-mono text-white/30 uppercase tracking-wider">
          {highlight ? "Latest Report" : "Report"}
        </p>
        <div className="flex items-center gap-2">
          <span className="text-xs font-mono px-2 py-0.5 rounded-full bg-[#00ff88]/10 text-[#00ff88]/80">
            {new Date(report.created_at).toLocaleDateString()}
          </span>
          <button
            onClick={onDownload}
            className="text-[10px] font-mono px-2 py-1 rounded-md border border-white/10 text-white/70 hover:text-white hover:border-white/20"
          >
            ↓ Download PDF
          </button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2 mb-4">
        <Stat label="Pages this week" value={d.pages_published} />
        <Stat label="Avg ranking" value={d.avg_rank !== null ? `#${d.avg_rank}` : "—"} />
        <Stat
          label="GSC impressions"
          value={d.total_impressions !== null ? d.total_impressions.toLocaleString() : "—"}
        />
        <Stat label="Competitors" value={d.competitor_count} />
      </div>

      <div className="border-t border-white/5 pt-3">
        <p className="text-[10px] font-mono text-white/30 uppercase tracking-wider mb-2">
          Top performing keywords
        </p>
        {d.top_keywords.length === 0 ? (
          <p className="text-xs font-mono text-white/30">No ranking data this period.</p>
        ) : (
          <div className="space-y-1">
            {d.top_keywords.map((k, i) => (
              <div
                key={`${k.keyword}-${i}`}
                className="flex items-center justify-between text-xs font-mono"
              >
                <span className="text-white/70 truncate">
                  <span className="text-white/30 mr-2">{i + 1}.</span>
                  {k.keyword}
                </span>
                <span className="text-[#00ff88]/70 shrink-0 ml-2">
                  {k.rank !== null ? `#${k.rank}` : "—"}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </motion.div>
  );
};

const Stat = ({ label, value }: { label: string; value: string | number }) => (
  <div className="bg-white/[0.03] border border-white/5 rounded-md px-3 py-2">
    <p className="text-[9px] font-mono text-white/25 uppercase">{label}</p>
    <p className="text-sm font-mono text-white/80 mt-0.5">{String(value)}</p>
  </div>
);

const AiInsightCard = ({
  insight,
  generatedAt,
}: {
  insight: string;
  generatedAt: string | null;
}) => {
  const paragraphs = insight.split(/\n{2,}/).map((p) => p.trim()).filter(Boolean);
  return (
    <motion.div
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.05 }}
      className="relative overflow-hidden rounded-xl border border-purple-400/20 bg-gradient-to-br from-purple-500/[0.06] via-fuchsia-500/[0.04] to-transparent p-5"
    >
      <div className="absolute -top-16 -right-16 h-40 w-40 rounded-full bg-purple-500/10 blur-3xl pointer-events-none" />
      <div className="flex items-center justify-between mb-3 relative">
        <div className="flex items-center gap-2">
          <span className="inline-flex items-center gap-1 rounded-full border border-purple-300/30 bg-purple-500/10 px-2 py-0.5 text-[10px] font-mono uppercase tracking-wider text-purple-200">
            <span>✨</span> AI Insight
          </span>
          <span className="text-[10px] font-mono text-white/30">
            Your week, in plain English
          </span>
        </div>
        {generatedAt && (
          <span className="text-[10px] font-mono text-white/30">
            {new Date(generatedAt).toLocaleDateString()}
          </span>
        )}
      </div>
      <div className="space-y-3 relative">
        {paragraphs.map((p, i) => (
          <p
            key={i}
            className="text-sm leading-relaxed text-white/80 font-light"
            style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
          >
            {p}
          </p>
        ))}
      </div>
    </motion.div>
  );
};

export default ReportsTab;

